import { useState, useEffect, useRef, useCallback } from "react";
import MobileMenu from "./MobileMenu";
import { Link, NavLink, useLocation } from "react-router-dom";
import { COMPANY } from "../data/company";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileToggleRef = useRef(null);
  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  const dropdownTimeoutRef = useRef(null);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDesktopDropdownOpen(false);
  }, [location.pathname]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setMobileMenuOpen(false);
        setDesktopDropdownOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Check if current path is a service page
  const isServicesActive = location.pathname.startsWith("/ydelser");

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setDesktopDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setDesktopDropdownOpen(false);
    }, 150);
  };

  return (
    <header className="sticky top-0 z-40 bg-[#FFFFFF]/95 backdrop-blur-md border-b border-[#E5E5E0] transition-colors shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
      {/* Top utility bar */}
      <div className="bg-[#111111] text-white text-xs py-2 border-b border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2 text-neutral-300">
              <i className="fa-solid fa-location-dot text-[#A78E51]" aria-hidden="true" />
              <span>{COMPANY.address}</span>
            </span>
            <span className="hidden sm:flex items-center gap-2 text-neutral-400">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#A78E51]"></span>
              <span>CVR: {COMPANY.cvr}</span>
            </span>
          </div>

          <div className="flex items-center gap-5">
            <a
              href={COMPANY.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Besøg os på Facebook"
              className="text-neutral-300 hover:text-[#A78E51] transition-colors flex items-center gap-1.5 py-0.5"
            >
              <i className="fa-brands fa-facebook-f" aria-hidden="true" />
              <span className="hidden md:inline">Facebook</span>
            </a>
            <div className="h-3 w-px bg-neutral-700 hidden sm:block" />
            <a
              href={COMPANY.phoneLink}
              className="text-white hover:text-[#A78E51] font-medium transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-[#A78E51]" aria-hidden="true" />
              <span>{COMPANY.phone}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo brand */}
          <Link
            to="/"
            className="flex items-center gap-3.5 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A78E51] rounded-md py-1"
          >
            <img
              src="https://assets.auxodev.dk/assets/3fc68df7-bc32-4dbe-a468-aab3c89985d1/ag-monogram-med-sort-tagkontur.png"
              alt="Tømrermester Andreas Guldmann logo"
              className="w-11 h-11 object-contain"
            />
            <div className="flex flex-col">
              <span className="font-bold text-base sm:text-lg leading-tight tracking-tight text-[#111111] transition-colors">
                Tømrermester
              </span>
              <span className="font-bold text-base sm:text-lg leading-tight tracking-tight text-[#A78E51]">
                Andreas Guldmann
              </span>
            </div>
          </Link>

          {/* Desktop Navigation (>= 1200px) */}
          <nav
            className="nav-desktop items-center gap-1 xl:gap-2"
            aria-label="Hovednavigation"
          >
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-semibold rounded-md transition-colors ${
                  isActive
                    ? "text-[#111111] bg-neutral-100 font-bold"
                    : "text-neutral-700 hover:text-[#111111] hover:bg-neutral-50"
                }`
              }
            >
              Forside
            </NavLink>

            {/* Ydelser with dropdown */}
            <div
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div className="flex items-stretch">
                <NavLink
                  to="/ydelser"
                  className={({ isActive }) =>
                    `px-3.5 py-2 text-sm font-semibold rounded-l-md transition-colors ${
                      isActive || isServicesActive
                        ? "text-[#111111] bg-neutral-100 font-bold border-b-2 border-[#A78E51]"
                        : "text-neutral-700 hover:text-[#111111] hover:bg-neutral-50"
                    }`
                  }
                >
                  Ydelser
                </NavLink>
                <button
                  type="button"
                  onClick={() => setDesktopDropdownOpen(!desktopDropdownOpen)}
                  aria-expanded={desktopDropdownOpen}
                  aria-haspopup="true"
                  aria-label="Åbn menu for ydelser"
                  className={`flex items-center justify-center p-2 text-neutral-600 hover:text-[#111111] rounded-r-md transition-colors ${
                    isServicesActive ? "bg-neutral-100 border-b-2 border-[#A78E51]" : "hover:bg-neutral-50"
                  }`}
                >
                  <i
                    className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                      desktopDropdownOpen ? "rotate-180" : ""
                    }`}
                    aria-hidden="true"
                  />
                </button>
              </div>

              {/* Dropdown panel */}
              {desktopDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-neutral-200 py-2 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                  <div className="px-3 py-1.5 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                    Vores specialeområder
                  </div>
                  {COMPANY.services.map((svc) => (
                    <NavLink
                      key={svc.slug}
                      to={svc.path}
                      className={({ isActive }) =>
                        `flex items-start gap-3 px-3 py-2.5 text-sm transition-colors ${
                          isActive
                            ? "bg-neutral-100 text-[#111111] font-bold border-l-4 border-[#A78E51]"
                            : "text-neutral-700 hover:bg-neutral-50 hover:text-[#111111]"
                        }`
                      }
                    >
                      <i
                        className={`${svc.icon} text-[#A78E51] mt-1 text-sm`}
                        aria-hidden="true"
                      />
                      <div>
                        <div className="font-medium text-[#111111]">{svc.title}</div>
                        <div className="text-xs text-neutral-500 line-clamp-1">{svc.shortDesc}</div>
                      </div>
                    </NavLink>
                  ))}
                  <div className="border-t border-neutral-100 mt-1 pt-1">
                    <NavLink
                      to="/ydelser"
                      className="block px-3 py-2 text-xs font-semibold text-[#A78E51] hover:text-[#937B43] hover:bg-neutral-50"
                    >
                      Se alle ydelser &rarr;
                    </NavLink>
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/galleri"
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-semibold rounded-md transition-colors ${
                  isActive
                    ? "text-[#111111] bg-neutral-100 font-bold"
                    : "text-neutral-700 hover:text-[#111111] hover:bg-neutral-50"
                }`
              }
            >
              Galleri
            </NavLink>

            <NavLink
              to="/om-andreas"
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-semibold rounded-md transition-colors ${
                  isActive
                    ? "text-[#111111] bg-neutral-100 font-bold"
                    : "text-neutral-700 hover:text-[#111111] hover:bg-neutral-50"
                }`
              }
            >
              Om Andreas
            </NavLink>

            {/* Header phone & CTA */}
            <div className="ml-4 flex items-center gap-3">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center px-4 py-2.5 rounded-md text-sm font-bold text-white bg-[#A78E51] hover:bg-[#937B43] border border-[#A78E51]/40 shadow-sm transition-all hover:border-[#A78E51]"
              >
                <span>Kontakt Andreas</span>
                <i className="fa-solid fa-arrow-right ml-2 text-white text-xs" aria-hidden="true" />
              </Link>
            </div>
          </nav>

          {/* Mobile hamburger toggle (< 1200px) */}
          <div className="nav-mobile-toggle items-center gap-3">
            <button
              ref={mobileToggleRef}
              aria-controls="mobile-navigation"
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2.5 rounded-md text-neutral-800 hover:text-[#111111] hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-[#A78E51]"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? "Luk menu" : "Åbn menu"}
            >
              <i
                className={`fa-solid ${mobileMenuOpen ? "fa-xmark" : "fa-bars"} text-xl`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      <MobileMenu open={mobileMenuOpen} onClose={closeMobileMenu} triggerRef={mobileToggleRef} />
    </header>
  );
}
